import React from 'react';
import { Brodcrumb } from '../../components/ui/UtilsLayout';
import { iconsPath, imagePath, videoPath } from '../../utils/imagePath';
import { BackgroundWithCurve, BulletSection, CaseStudiesImageCard, CaseStudyHeading, Section } from '../../utils/CaseStudiesComponents';

// CaseStudy Component
const CarMechanicTraining = () => {
  return (
    <div className="flex justify-center px-5 md:px-10 lg:px-16  items-center gap-6 text-white pb-16 relative">
      <BackgroundWithCurve />
      <div className="max-w-[1200px] w-full  pt-24 md:pt-32 relative z-10">
        {/* Breadcrumb Navigation */}
        <Brodcrumb
          data={{
            name: 'Car Mechanic Trianing',
            item: [
              { name: 'Industry Use Cases', path: '/all-industries' },
              {
                name: 'ManuFacturing & Industrial',
                path: '/industries/manufacturing-industrial',
              },
            ],
          }}
        />

        <div className="my-10 flex flex-col w-full gap-10">
          {/* Header Section with Logo */}
          <div className="flex gap-3 md:gap-5 lg:gap-10">
            <div className="flex justify-center items-center  bg-gradient-to-t from-[#444447] to-[#292929] shadow-[0px_0px_4px_1px_rgba(0,0,0,0.25)] h-16 w-16 md:h-20 md:w-20 lg:h-24 lg:w-24 rounded-lg">
              {/* Company Logo */}
              {imagePath?.CarMechanicLogo ? (
                <img
                  className="h-12 w-12 md:h-16 md:w-16 lg:h-16 lg:w-16 object-contain"
                  src={imagePath?.CarMechanicLogo}
                  alt="CarMechanic Logo"
                />
              ) : (
                <span className="text-gray-400 text-xs">No Logo</span>
              )}
            </div>

            {/* Case Study Heading */}
            <div className="flex flex-col gap-2 md:gap-5 justify-center">
              <CaseStudyHeading
                title="Car Mechanic Training"
                desc="Automotive OEM / Service Network"
              />
            </div>
          </div>

          <div className="relative">
            {/* Video Container - Responsive aspect ratio */}
            <div className="relative w-full aspect-video bg-[#31333A] shadow-[0px_0px_21px_4px_rgba(0,0,0,0.25)] flex justify-center items-center ">
              {/* Play Icon */}
              {videoPath?.CarMachenicVideo && (
                <video
                  src={videoPath.CarMachenicVideo}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover rounded-lg"
                >
                  Your browser does not support the video tag.
                </video>
              )}

              {/* {iconsPath?.QRIcon && (
                <div className="absolute -top-4 -right-4 md:-top-6 md:-right-6 lg:-top-8 lg:-right-8 bg-[#fff] p-2 md:p-3 rounded-[10px]">
                  <img
                    src={iconsPath.QRIcon}
                    alt="QR Code"
                    className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24"
                  />
                </div>
              )} */}
            </div>
          </div>

          {/* Case Study Description */}
          <Section
            title="Overview"
            // subtitle="Web based AR experience for festivals."
            content={[
              'Improving skill proficiency, accuracy, and retention for automotive technicians through immersive training.',
              'One of the key challenges faced by automotive OEMs and large service networks is ensuring consistent skill levels across mechanics, especially when dealing with complex vehicle systems, evolving technologies, and safety-critical procedures. Traditional classroom training and workshop-based demonstrations often limit repeatability, scalability, and objective assessment of technician engagement and understanding.',
              'The VR Car Mechanic Training Module was designed to deliver a **hands-on, immersive learning experience** that enables trainees and professionals to understand vehicle systems, tools, and repair workflows in a controlled, repeatable virtual environment—while allowing instructors to assess engagement, attention span, and procedural accuracy in real time.',
            ]}
          />
          <BulletSection
            title="VR Experience Interface"
            bulletPoints={[
              'Interactive vehicle and component visualization',
              'Step-by-step guided repair and maintenance workflows',
              'Virtual tool handling and safety checks',
              'Real-time instructor monitoring and trainee performance tracking',
            ]}
          />

          <Section
            title="Execution"
            content={[
              'Spatial Grid developed a comprehensive **VR-based automotive mechanic training solution** covering core vehicle maintenance, diagnostics, and repair procedures. The program was structured into **modular training courses**, each aligned with standard automotive service workflows and OEM-recommended procedures.',
              'Each module was supported by **step-by-step interactive storyboards**, ensuring intuitive understanding of mechanical processes such as engine components, braking systems, suspension, electrical systems, and routine service operations. The framework was designed to support multiple skill levels, from entry-level technicians to experienced mechanics, with flexibility for localization and language support.',
              'High-fidelity **3D vehicle models and workshop environments** were created to closely replicate real-world service bays. Components were designed to be interactive—allowing users to assemble, disassemble, inspect, and troubleshoot parts using virtual tools. Detailed simulations and animations demonstrated correct procedures, torque sequences, safety checks, and common failure scenarios.',
              'The experience was optimized for VR headsets and included a **multi-player instructor-led mode**, enabling trainers to guide sessions, observe technician actions, track errors, and evaluate engagement and task completion in real time. This allowed instructors to conduct walkthroughs, assessments, and collaborative learning sessions without the constraints of physical vehicles or workshop availability.',
            ]}
          />

          {/* Image Grid Section */}
          <div className="flex flex-col gap-3">
            <div className="flex md:flex-row flex-col gap-10 md:gap-20 w-full">
              <CaseStudiesImageCard
                image={imagePath?.CarMachenic1}
                title="AR Experience Interface"
              />
              <CaseStudiesImageCard
                image={imagePath?.CarMachenic2}
                title="Hands-On Mechanic Training"
              />
            </div>
          </div>

          {/* Impact Section */}
          <div className="flex flex-col gap-3">
            <BulletSection
              title="Impact"
              bulletPoints={[
                'Significant improvement in **concept clarity and procedural understanding**',
                'Higher **knowledge retention** through hands-on, repeatable practice',
                'Reduced dependency on physical vehicles for training',
                'Quantifiable insights into technician performance, errors, and learning gaps',
                'Data-driven refinement of training content and instructional methods',
              ]}
              closingText="Observations and analytics generated from the VR modules enabled the organization to identify areas where trainees struggled—such as incorrect tool usage, skipped safety steps, or improper assembly sequences—and refine training material accordingly. The customer is planning to expand the program with **advanced diagnostic scenarios, electric vehicle (EV) modules, and certification-based assessments.**"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarMechanicTraining;
